import React, { useState } from 'react';
import { Test } from '../../types/test';

interface TestExecutionModalProps {
  isOpen: boolean;
  test: Test | null;
  messages: Array<{ content: string; type: 'user' | 'assistant' | 'system' }>;
  toolCalls: Array<{ 
    id: string; 
    function_name: string; 
    timestamp: Date; 
    status: 'pending' | 'completed';
    call_id?: string;
  }>;
  isRunning: boolean;
  onClose: () => void;
}

const TestExecutionModal: React.FC<TestExecutionModalProps> = ({ 
  isOpen, 
  test, 
  messages, 
  toolCalls = [],
  isRunning, 
  onClose 
}) => {
  const [showToolCalls, setShowToolCalls] = useState(true);
  
  if (!isOpen || !test) return null;
  
  const pendingTools = toolCalls.filter(call => call.status === 'pending').length;
  const completedTools = toolCalls.filter(call => call.status === 'completed').length;

  const getMessageStyle = (type: string) => {
    switch (type) {
      case 'user':
        return 'bg-blue-800/20 border-blue-600 text-blue-200';
      case 'assistant':
        return 'bg-zinc-800/50 border-zinc-600 text-zinc-200';
      case 'system':
        return 'bg-amber-800/20 border-amber-600 text-amber-200';
      default:
        return 'bg-zinc-800/50 border-zinc-600 text-zinc-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg max-w-4xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div>
            <h2 className="text-2xl font-bold text-zinc-200">Test Execution: {test.name}</h2>
            <p className="text-zinc-400 text-sm mt-1">{test.task}</p>
          </div>
          <div className="flex items-center gap-4">
            {isRunning && (
              <div className="flex items-center gap-2 text-amber-400">
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="text-sm">Running...</span>
              </div>
            )}
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tool Calls Section */}
        {toolCalls.length > 0 && (
          <div className="border-b border-zinc-800 px-6 py-4">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowToolCalls(!showToolCalls)}
            >
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-zinc-200">Tool Calls</h3>
                <div className="flex items-center gap-2 text-sm">
                  <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded">
                    {completedTools} completed
                  </span>
                  {pendingTools > 0 && (
                    <span className="px-2 py-1 bg-amber-900/30 text-amber-400 rounded">
                      {pendingTools} pending
                    </span>
                  )}
                </div>
              </div>
              <svg 
                className={`w-5 h-5 text-zinc-400 transform transition-transform ${showToolCalls ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            
            {showToolCalls && (
              <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                {toolCalls.map((call) => (
                  <div 
                    key={call.id} 
                    className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {call.status === 'pending' ? (
                        <svg className="w-5 h-5 text-amber-400 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span className="text-zinc-200 font-mono text-sm">{call.function_name}</span>
                    </div>
                    <span className="text-zinc-500 text-xs">
                      {call.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`p-4 rounded-lg border ${getMessageStyle(message.type)}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium uppercase tracking-wide opacity-75">
                  {message.type}
                </span>
              </div>
              <div className="whitespace-pre-wrap font-mono text-sm break-words">
                {message.content}
              </div>
            </div>
          ))}
          
          {messages.length === 0 && (
            <div className="text-center text-zinc-400 py-8">
              <svg className="w-12 h-12 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p>No messages yet...</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestExecutionModal;